import React, { useState, useEffect } from 'react';
import { 
  Typography, Card, CardHeader, CardContent, Avatar,
   Grid, TextField, Button, Box, Chip, Paper,
  Divider, FormControl, InputLabel, Select, MenuItem, Stack,
   Checkbox, InputAdornment, ButtonGroup,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow,
  LinearProgress, useMediaQuery, useTheme,
  Collapse, Snackbar, Alert, Accordion, AccordionSummary, AccordionDetails,
  Tooltip, Badge, TablePagination, CircularProgress
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Computer as ComputerIcon,
  Speed as SpeedIcon,
  Map as MapIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MapChart from '../cloudmanagement/MapChart';
import ServerCard from '../cloudmanagement/ServerCard';
import ServerTableRow from '../cloudmanagement/ServerTableRow';
import AddNodeForm from '../cloudmanagement/AddNodeForm';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  getNodes, getNodeById, deleteNode, transformNodeToDisplayFormat
} from '../api/services/computeService/NodeService';
import { enhanceNodeWithLocationData } from '../api/services/computeService/LocationService';
import { getClientNodes } from '../api/services/computeService/ClientNodeService';
import { getDepartmentNodes } from '../api/services/computeService/DepartmentNodeService';
import { getTeamNodes } from '../api/services/computeService/TeamNodeService';
import { getUserNodes } from '../api/services/computeService/UserNodeService';
import { useKeycloak } from '@react-keycloak/web';
import { useUser } from '../../contexts/UserContext';



const StatCard = ({ icon, title, value, subtitle, color, trend }) => {
  const theme = useTheme();

  return (
  <Card sx={{ 
    height: '100%', 
      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[900], theme.palette.mode === 'dark' ? 0.8 : 0.1)} 0%, ${alpha(theme.palette.grey[800], theme.palette.mode === 'dark' ? 0.8 : 0.1)} 100%)`,
    borderRadius: 1,
      border: `1px solid ${theme.palette.divider}`,
    transition: 'transform 0.2s',
    '&:hover': { transform: 'translateY(-1px)' }
  }}>
    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{
          p: 0.4,
          borderRadius: 0.5,
          bgcolor: `${color}15`,
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 28,
          height: 28
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 16 } })}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              display: 'block', 
              lineHeight: 1,
              fontSize: '0.65rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 'bold', 
              lineHeight: 1.1,
              fontSize: '0.9rem',
              whiteSpace: 'nowrap'
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                lineHeight: 1,
                fontSize: '0.6rem',
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);
};

export default function CloudManagement() {
  const [showAddNodeForm, setShowAddNodeForm] = useState(false);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedServers, setSelectedServers] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    region: 'all',
    type: 'all',
    gpuCount: 'all'
  });
  const [viewType, setViewType] = useState('tiles');
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');
  const [deletingNodes, setDeletingNodes] = useState(new Set());
  const [pendingOperations, setPendingOperations] = useState(new Set());
  const [mapExpanded, setMapExpanded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { keycloak } = useKeycloak();
  const { isAdmin, isSuperAdmin, user, userPermissions } = useUser();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const canManageClientNodes = () => {
    if (isSuperAdmin) return true;
    if (userPermissions?.clientRole === 'admin') return true;
    return false;
  };

  const calculateStatistics = () => {
    const stats = {
      totalNodes: servers.length,
      activeNodes: servers.filter(s => s.status === 'Active').length,
      failedNodes: servers.filter(s => s.status === 'Registration Failed').length,
      registeringNodes: servers.filter(s => s.status === 'Registering').length,
      totalGPUs: servers.reduce((sum, s) => sum + (s.numGpus || 0), 0),
      totalCPUs: servers.reduce((sum, s) => sum + (s.numOfCpus || 0), 0),
      totalMemory: servers.reduce((sum, s) => sum + (s.mem || 0), 0),
      totalStorage: servers.reduce((sum, s) => sum + (s.storage || 0), 0),
      gpuVendors: {},
      nodeTypes: {},
      regions: {}
    };

    servers.forEach(server => {
      // Handle mixed GPU types properly
      if (server.gpuBreakdown && server.gpuBreakdown.length > 0) {
        server.gpuBreakdown.forEach(gpu => {
          // Extract vendor from GPU model (basic heuristic)
          let vendor = 'Unknown';
          if (gpu.model.toLowerCase().includes('rtx') || gpu.model.toLowerCase().includes('gtx') || gpu.model.toLowerCase().includes('tesla') || gpu.model.toLowerCase().includes('quadro')) {
            vendor = 'NVIDIA';
          } else if (gpu.model.toLowerCase().includes('radeon') || gpu.model.toLowerCase().includes('rx ')) {
            vendor = 'AMD';
          } else if (gpu.model.toLowerCase().includes('intel') || gpu.model.toLowerCase().includes('arc')) {
            vendor = 'Intel';
          }
          stats.gpuVendors[vendor] = (stats.gpuVendors[vendor] || 0) + gpu.count;
        });
      } else if (server.gpu && server.gpu !== 'None') {
        // Fallback to old method if gpuBreakdown is not available
        stats.gpuVendors[server.gpu] = (stats.gpuVendors[server.gpu] || 0) + (server.numGpus || 1);
      }
      
      if (server.type) {
        stats.nodeTypes[server.type] = (stats.nodeTypes[server.type] || 0) + 1;
      }
      if (server.region) {
        stats.regions[server.region] = (stats.regions[server.region] || 0) + 1;
      }
    });

    return stats;
  };

  const stats = calculateStatistics();

  useEffect(() => {
    const initializeServers = async () => {
      if (!keycloak.token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        try {
          await keycloak.updateToken(30);
        } catch (tokenError) {
          console.error('Token refresh failed:', tokenError);
          keycloak.logout({ redirectUri: window.location.origin });
          return;
        }
        
        let nodeIds = [];

        if (isSuperAdmin) {
          const response = await getNodes({
            paginationParams: { page: 0, page_size: 200 },
            token: keycloak.token
          });
          
          if (response.success && response.data.nodes) {
            nodeIds = response.data.nodes.map(node => node.id || node.nodeId).filter(id => id);
          }
        } else if (userPermissions) {
          if (userPermissions.teamIds && userPermissions.teamIds.length > 0) {
            const response = await getTeamNodes({
              teamId: userPermissions.teamIds[0],
              token: keycloak.token
            });
            if (response.success && response.data.nodeIds) {
              nodeIds = response.data.nodeIds;
            }
          } else if (userPermissions.departmentIds && userPermissions.departmentIds.length > 0) {
            const response = await getDepartmentNodes({
              departmentId: userPermissions.departmentIds[0],
              token: keycloak.token
            });
            if (response.success && response.data.nodeIds) {
              nodeIds = response.data.nodeIds;
            }
          } else if (userPermissions.clientIds && userPermissions.clientIds.length > 0) {
            const response = await getClientNodes({
              clientId: userPermissions.clientIds[0],
              token: keycloak.token
            });
            if (response.success && response.data.nodeIds) {
              nodeIds = response.data.nodeIds;
            }
          } else if (user && user.id) {
            const response = await getUserNodes({
              userId: user.id,
              token: keycloak.token
            });
            if (response.success && response.data.nodeIds) {
              nodeIds = response.data.nodeIds;
            }
          }
        }
        
        if (nodeIds.length > 0) {
          const detailedNodes = await Promise.all(
            nodeIds.map(async (nodeId) => {
              try {
                const nodeResponse = await getNodeById(nodeId, keycloak.token);
                if (nodeResponse.success && nodeResponse.data) {
                  return transformNodeToDisplayFormat(nodeResponse.data);
                }
                return null;
              } catch (error) {
                console.warn(`Failed to fetch details for node ${nodeId}:`, error);
                return null;
              }
            })
          );

          const validNodes = detailedNodes.filter(node => node !== null);
          const enhancedNodes = validNodes.map(node => enhanceNodeWithLocationData(node));
          
          setServers(enhancedNodes);
          
          const hasRegisteringNodes = enhancedNodes.some(node => node.status === 'Registering');
          if (hasRegisteringNodes && !autoRefreshEnabled) {
            setAutoRefreshEnabled(true);
            setRefreshMessage('Auto-refresh enabled: Some nodes are still being set up');
          }
        } else {
          setServers([]);
        }
      } catch (error) {
        console.error('Error initializing servers:', error);
        setError(error.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    initializeServers();
  }, [keycloak.token, isSuperAdmin, userPermissions, user]);

  useEffect(() => {
    let intervalId;
    
    if (autoRefreshEnabled || pendingOperations.size > 0) {
      intervalId = setInterval(async () => {
        await handleRefresh(true);
      }, 10000);
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefreshEnabled, pendingOperations.size, keycloak.token, isSuperAdmin, userPermissions, user]);



  const handleRefresh = async (isAutoRefresh = false) => {
    if (!keycloak.token) {
      setError('Authentication required');
      return;
    }

    if (!isAutoRefresh) setLoading(true);
    
    try {
      try {
        await keycloak.updateToken(30);
      } catch (tokenError) {
        console.error('Token refresh failed:', tokenError);
        keycloak.logout({ redirectUri: window.location.origin });
        return;
      }
      
      let nodeIds = [];

      if (isSuperAdmin) {
        const response = await getNodes({
          paginationParams: { page: 0, page_size: 200 },
          token: keycloak.token
        });
        
        if (response.success && response.data.nodes) {
          nodeIds = response.data.nodes.map(node => node.id || node.nodeId).filter(id => id);
        }
      } else if (userPermissions) {
        if (userPermissions.teamIds && userPermissions.teamIds.length > 0) {
          const response = await getTeamNodes({
            teamId: userPermissions.teamIds[0],
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        } else if (userPermissions.departmentIds && userPermissions.departmentIds.length > 0) {
          const response = await getDepartmentNodes({
            departmentId: userPermissions.departmentIds[0],
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        } else if (userPermissions.clientIds && userPermissions.clientIds.length > 0) {
          const response = await getClientNodes({
            clientId: userPermissions.clientIds[0],
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        } else if (user && user.id) {
          const response = await getUserNodes({
            userId: user.id,
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        }
      }
      
      if (nodeIds.length > 0) {
        const detailedNodes = await Promise.all(
          nodeIds.map(async (nodeId) => {
            try {
              const nodeResponse = await getNodeById(nodeId, keycloak.token);
              if (nodeResponse.success && nodeResponse.data) {
                return transformNodeToDisplayFormat(nodeResponse.data);
              }
              return null;
            } catch (error) {
              console.warn(`Failed to fetch details for node ${nodeId}:`, error);
              return null;
            }
          })
        );

        const validNodes = detailedNodes.filter(node => node !== null);
        const enhancedNodes = validNodes.map(node => enhanceNodeWithLocationData(node));
        
        setServers(enhancedNodes);
        setError(null);
      } else {
        setServers([]);
        setError(null);
      }
    } catch (error) {
      console.error('Refresh failed:', error);
      setError('Failed to refresh server data');
    } finally {
      if (!isAutoRefresh) setLoading(false);
    }
  };

  const handleDeleteServer = async (serverId) => {
    if (!keycloak.token) {
      setError('Authentication required');
      return;
    }

    if (!canManageClientNodes()) {
      setError('Only super admins and client admins can delete nodes');
      return;
    }

    try {
      setDeletingNodes(prev => new Set([...prev, serverId]));
      setPendingOperations(prev => new Set([...prev, serverId]));
      
      await keycloak.updateToken(30);
      const response = await deleteNode(serverId, keycloak.token);
      
      if (response.success) {
        setRefreshMessage('Node deletion in progress...');
        setTimeout(() => handleRefresh(), 2000);
              } else {
        setError(response.error || 'Failed to delete node');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      setError('Failed to delete server');
    } finally {
      setDeletingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(serverId);
        return newSet;
      });
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(serverId);
        return newSet;
      });
    }
  };

  const filteredServers = servers.filter(server => {
    const matchesSearch = server.hostname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         server.ipAddress?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         server.id?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filters.status === 'all' || 
                         (filters.status === 'active' && server.status === 'Active') ||
                         (filters.status === 'failed' && server.status === 'Registration Failed') ||
                         (filters.status === 'registering' && server.status === 'Registering');
    
    const matchesRegion = filters.region === 'all' || server.region === filters.region;
    const matchesType = filters.type === 'all' || server.type?.toLowerCase().includes(filters.type.toLowerCase());
    
    const matchesGPUs = filters.gpuCount === 'all' ||
                       (filters.gpuCount === '0' && server.numGpus === 0) ||
                       (filters.gpuCount === '1-4' && server.numGpus >= 1 && server.numGpus <= 4) ||
                       (filters.gpuCount === '5-8' && server.numGpus >= 5 && server.numGpus <= 8) ||
                       (filters.gpuCount === '8+' && server.numGpus > 8);
    
    return matchesSearch && matchesStatus && matchesRegion && matchesType && matchesGPUs;
  });

  const paginatedServers = filteredServers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleServerSelect = (serverId) => {
    const server = servers.find(s => s.id === serverId);
    if (server && (server.status === 'Registering' || deletingNodes.has(serverId) || pendingOperations.has(serverId))) return;
    
    setSelectedServers(prev => 
      prev.includes(serverId) 
        ? prev.filter(id => id !== serverId)
        : [...prev, serverId]
    );
  };

  const handleBulkDelete = () => {
    if (!canManageClientNodes()) {
      setError('Only super admins and client admins can delete nodes');
      return;
    }

    const confirmMessage = `Delete ${selectedServers.length} selected node(s)?`;
    if (window.confirm(confirmMessage)) {
      selectedServers.forEach(serverId => handleDeleteServer(serverId));
      setSelectedServers([]);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading nodes...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: { xs: 2, sm: 3 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <StatCard
            icon={<ComputerIcon />}
            title="Total Nodes"
            value={stats.totalNodes}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <StatCard
            icon={<CheckCircleIcon />}
            title="Active"
            value={stats.activeNodes}
            subtitle={stats.totalNodes > 0 ? `${Math.round((stats.activeNodes / stats.totalNodes) * 100)}% healthy` : ''}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <StatCard
            icon={<ErrorIcon />}
            title="Failed"
            value={stats.failedNodes}
            color={theme.palette.error.main}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <StatCard
            icon={<SpeedIcon />}
            title="Total GPUs"
            value={stats.totalGPUs}
            color={theme.palette.info.main}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <StatCard
            icon={<MemoryIcon />}
            title="Memory"
            value={`${Math.round(stats.totalMemory)}GB`}
            color={theme.palette.secondary.main}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <StatCard
            icon={<StorageIcon />}
            title="Storage"
            value={(() => {
              if (stats.totalStorage >= 1000) {
                return `${(stats.totalStorage / 1000).toFixed(1)}TB`;
              }
              return `${stats.totalStorage}GB`;
            })()}
            color={theme.palette.warning.main}
          />
        </Grid>
      </Grid>

      <Accordion 
        expanded={mapExpanded} 
        onChange={() => setMapExpanded(!mapExpanded)}
        sx={{ mb: 2, bgcolor: 'background.paper', borderRadius: 1 }}
          >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
            sx={{
            minHeight: 44,
            '&.Mui-expanded': { minHeight: 44 },
            '& .MuiAccordionSummary-content': { 
              margin: '6px 0',
              '&.Mui-expanded': { margin: '6px 0' }
            },
            py: 0.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <MapIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Node Locations Map</Typography>
            <Chip label={`${stats.totalNodes} nodes`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
        </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ height: 600, width: '100%' }}>
            <MapChart servers={servers} />
      </Box>
        </AccordionDetails>
      </Accordion>

      <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by hostname, IP, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="registering">Registering</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Region</InputLabel>
                    <Select
                value={filters.region}
                onChange={(e) => setFilters({ ...filters, region: e.target.value })}
                      label="Region"
                    >
                      <MenuItem value="all">All Regions</MenuItem>
                {Object.keys(stats.regions).map(region => (
                  <MenuItem key={region} value={region}>
                    {region} ({stats.regions[region]})
                  </MenuItem>
                ))}
                    </Select>
                  </FormControl>
                </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>GPU Count</InputLabel>
                    <Select
                value={filters.gpuCount}
                onChange={(e) => setFilters({ ...filters, gpuCount: e.target.value })}
                label="GPU Count"
                    >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="0">No GPUs</MenuItem>
                <MenuItem value="1-4">1-4 GPUs</MenuItem>
                <MenuItem value="5-8">5-8 GPUs</MenuItem>
                <MenuItem value="8+">8+ GPUs</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
              <ToggleButtonGroup
                value={viewType}
                exclusive
                onChange={(e, newView) => newView && setViewType(newView)}
                size="small"
              fullWidth
              >
              <ToggleButton value="tiles">
                <GridViewIcon />
                </ToggleButton>
              <ToggleButton value="list">
                <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title={autoRefreshEnabled ? "Auto-refresh active" : "Refresh"}>
                <IconButton onClick={() => handleRefresh()} disabled={loading} size="small" sx={{ height: 40 }}>
                  <Badge variant="dot" color="success" invisible={!autoRefreshEnabled}>
                    <RefreshIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              {canManageClientNodes() && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddNodeForm(true)} size="small" sx={{ height: 40 }}>
                  Add Node
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      
      {selectedServers.length > 0 && canManageClientNodes() && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'warning.dark', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">
              {selectedServers.length} node(s) selected
          </Typography>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
              size="small"
              sx={{ py: 0.5 }}
          >
              Delete Selected
          </Button>
            </Box>
          </Paper>
      )}

      {viewType === 'tiles' ? (
        <>
          <Grid container spacing={2}>
            {paginatedServers.map((server) => (
            <Grid item xs={12} sm={6} lg={4} key={server.id}>
              <ServerCard
                server={server}
                isSelected={selectedServers.includes(server.id)}
                isDeleting={deletingNodes.has(server.id)}
                hasPendingOperation={pendingOperations.has(server.id)}
                onSelect={handleServerSelect}
                onDelete={canManageClientNodes() ? handleDeleteServer : undefined}
                showMonitor={canManageClientNodes()}
                onRefresh={handleRefresh}
              />
            </Grid>
          ))}
        </Grid>
          <TablePagination
            component="div"
            count={filteredServers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[6, 12, 24, 48]}
            sx={{ mt: 2 }}
          />
        </>
      ) : (
        <Paper sx={{ bgcolor: 'background.paper', borderRadius: 1, overflow: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedServers(filteredServers.map(s => s.id));
                      } else {
                        setSelectedServers([]);
                      }
                    }}
                    checked={selectedServers.length === filteredServers.length && filteredServers.length > 0}
                    indeterminate={selectedServers.length > 0 && selectedServers.length < filteredServers.length}
                  />
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Hostname / ID</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>CPU/GPU</TableCell>
                <TableCell>CPUs</TableCell>
                <TableCell>GPUs</TableCell>
                <TableCell>Memory</TableCell>
                <TableCell>Storage</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedServers.map((server) => (
                <ServerTableRow
                  key={server.id}
                  server={server}
                  isSelected={selectedServers.includes(server.id)}
                  isDeleting={deletingNodes.has(server.id)}
                  hasPendingOperation={pendingOperations.has(server.id)}
                  onSelect={handleServerSelect}
                  onDelete={canManageClientNodes() ? handleDeleteServer : undefined}
                  showMonitor={canManageClientNodes()}
                  onRefresh={handleRefresh}
                />
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredServers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      <AddNodeForm
        open={showAddNodeForm}
        onClose={() => setShowAddNodeForm(false)}
        onSuccess={(nodeData) => {
          setShowAddNodeForm(false);
          handleRefresh();
        }}
      />
      
      <Snackbar
        open={!!refreshMessage}
        onClose={() => setRefreshMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info">{refreshMessage}</Alert>
      </Snackbar>
    </Box>
  );
}
